// hooks/useInterestMatch.ts
// Prior-stack Phase 4E: which of the student's interest tags a course matches,
// for the "matches your interest: X" badge on suggestion cards. Computed
// client-side from the already-resolved tag→courses map (interestTagsService),
// so no new data has to flow through the suggestion pipeline.
//
// The resolved map is fetched once per session and cached at module scope —
// suggestion cards render many at a time, so a shared promise avoids a fetch
// per card. Failures resolve to an empty map (the badge is an enhancement,
// never a blocker).
import { useEffect, useState } from 'react';
import { interestTagsService } from '@/services/interestTagsService';

type MatchMap = Map<string, string>; // course code (upper) → interest tag

let cache: Promise<MatchMap> | null = null;

function buildMap(): Promise<MatchMap> {
  return interestTagsService
    .get()
    .then((data) => {
      const positive = new Set((data.interest_tags || []).map((t) => t.toLowerCase()));
      const map: MatchMap = new Map();
      for (const [tag, courses] of Object.entries(data.resolved || {})) {
        // Only positive interests earn a badge — avoidances are steer-away.
        if (!positive.has(tag.toLowerCase())) continue;
        for (const c of courses) {
          if (c.removed) continue; // curated-out by the student
          const key = c.code.toUpperCase();
          if (!map.has(key)) map.set(key, tag); // first (highest-scored) tag wins
        }
      }
      return map;
    })
    .catch(() => new Map<string, string>());
}

/** Reset the module cache (for tests + after a tag edit). */
export function resetInterestMatchCache(): void {
  cache = null;
}

export function useInterestMatch(): (courseCode: string) => string | null {
  const [map, setMap] = useState<MatchMap | null>(null);

  useEffect(() => {
    let alive = true;
    if (!cache) cache = buildMap();
    cache.then((m) => alive && setMap(m));
    return () => {
      alive = false;
    };
  }, []);

  return (courseCode: string) => {
    if (!map || !courseCode) return null;
    return map.get(courseCode.toUpperCase()) ?? null;
  };
}
